import React from "react";
import { Sparkles } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";

const replies = [
  { ts: "c. 70 AD", quote: "What things soever ye desire, when ye pray, believe that ye receive them, and ye shall have them.", src: "Christianity · Mark 11:24" },
  { ts: "c. 615 AD", quote: "If you are grateful, I will surely increase you.", src: "Islam · Qur'an 14:7" },
  { ts: "c. 250 BC", quote: "All that we are is the result of what we have thought: it is founded on our thoughts, it is made up of our thoughts.", src: "Buddhism · Dhammapada, v.1" },
  { ts: "c. 200 BC", quote: "Man is made by his belief. As he believes, so he is.", src: "Hinduism · Bhagavad Gita 17.3" },
  { ts: "c. 175 AD", quote: "The soul becomes dyed with the colour of its thoughts.", src: "Stoicism · Marcus Aurelius, Meditations" },
];

const RepliesSection: React.FC = () => {
  return (
    <section id="law" className="py-24 md:py-32 px-6 relative overflow-hidden">
      <div className="absolute inset-0 z-[-1] bg-cosmic-radial opacity-30" />

      <div className="container mx-auto max-w-5xl">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <ScrollReveal>
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-medium tracking-wide text-cosmic-200 border border-cosmic-400/30 bg-cosmic-900/50 backdrop-blur-sm mb-4">
              The universe always replies
            </span>
          </ScrollReveal>
          <ScrollReveal delay={100}>
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 text-gradient">
              It's been replying for thousands of years.
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={200}>
            <p className="text-white/70 text-lg leading-relaxed">
              Long before "manifestation" had a name, the universe was already replying, and
              every great tradition got the same message: what you hold as real on the inside is
              what takes shape on the outside.
            </p>
          </ScrollReveal>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {replies.map((r, i) => (
            <ScrollReveal key={i} delay={i * 80}>
              <div className="flex gap-3 items-start">
                <div className="w-10 h-10 rounded-full bg-cosmic-500/20 border border-cosmic-400/30 grid place-items-center text-cosmic-200 flex-none mt-1">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-white/60 mb-1.5">
                    The Universe <span className="text-white/40">replied · {r.ts}</span>
                  </div>
                  <div className="cosmic-card p-5 rounded-2xl rounded-tl-sm">
                    <p className="text-lg italic text-white/90 leading-snug">"{r.quote}"</p>
                    <p className="mt-3 text-[11px] font-medium uppercase tracking-[0.12em] text-cosmic-200">
                      {r.src}
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          ))}

          <ScrollReveal delay={400}>
            <div className="md:col-span-2 text-center border border-dashed border-cosmic-400/30 rounded-2xl px-6 py-6 bg-cosmic-900/30 backdrop-blur-sm">
              <p className="italic text-lg text-cosmic-200">
                Five traditions, centuries apart, all writing down the same message, by hands
                that never met. Almost like the universe really wants you to get it.
              </p>
            </div>
          </ScrollReveal>
        </div>

        <ScrollReveal delay={500}>
          <p className="max-w-2xl mx-auto mt-16 text-center text-lg text-white/70 leading-relaxed">
            You've been DMing the universe your whole life, whether you meant to or not, every
            worry, every want, every scrap of your attention. Mindmail just helps you send better
            ones.
          </p>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default RepliesSection;
